/**
 * The request lifecycle's messages: the composer, the SharePoint explorer, the
 * request list and the request page.
 *
 * Split out of `reviewRequests.ts` in Phase 2B, Task 8. That file was 499
 * lines — already past this repo's 400-line hard limit — before this half grew
 * the explorer and page-count copy below (the confirm, detail and dispatch
 * copy moved out again to their own sibling files for the same reason).
 *
 * NOT imported by any screen directly. `reviewRequests.ts` spreads this back
 * into the same `reviewRequests` namespace, so every key here is still
 * `reviewRequests.requests.*` and no import path anywhere changed — Phase 1's
 * five screens are untouched by the split.
 *
 * Same two constraints as the parent file: **English only** (deliberately —
 * see `reviewRequests.ts`), and the CSP-safe compiler means `{named}`
 * placeholders only, with count-dependent text as `_one` / `_other` sibling
 * keys the caller chooses between rather than vue-i18n's `|` plural syntax.
 *
 * Every code the backend sends — `unreviewable_reason`, `blocked`,
 * `pricing_basis`, `page_source` — is mapped to a sentence HERE rather than
 * emitted in English from a Rust handler, which is what makes it translatable
 * and what keeps a raw identifier off a SAM's screen.
 */
import { reviewRequestsConfirmMessages } from "@/locales/reviewRequests.confirm";
import { reviewRequestsDetailMessages } from "@/locales/reviewRequests.detail";
import { reviewRequestsDispatchMessages } from "@/locales/reviewRequests.dispatch";
import { reviewRequestsListMessages } from "@/locales/reviewRequests.list";
import { reviewRequestsProductStepMessages } from "@/locales/reviewRequests.productStep";
import { reviewRequestsScholarsMessages } from "@/locales/reviewRequests.scholars";

export const reviewRequestsComposerMessages = {
  // Task 11, Phase 2A: the scholar card spec 8.2 describes ("badges
  // carry the derived decision ... a line beneath carries the
  // reason"). Consumed by `Requests/resolutionRules.ts::scholarCardFor`.
  // Deliberately a separate bucket from `terms.arrangements` (which stayed
  // in `reviewRequests.ts`) —
  // that one labels an EDITABLE arrangement picker; this one labels a
  // READ-ONLY derived badge, and the two screens are allowed to word
  // the same six values slightly differently without one drifting the
  // other by accident.
  requests: {
    arrangement: {
      srb_employee: "SRB employee",
      retainer_inclusive: "Retainer includes review fee",
      retainer_plus_fee: "Retainer plus per-review fee",
      fixed_value_review: "Fixed value per review",
      zero_value_review: "Zero value",
      monthly_quota: "Monthly quota",
    },
    basis: {
      zero: "No charge",
      fixed: "Fixed value",
      category: "By category band",
    },
    ssbBadgeMember: "SSB member",
    ssbBadgeNotMember: "Not an SSB member",
    reasonSsb: "On this client's SSB roster",
    reasonNotSsb: "Not on this client's SSB roster",
    // No `{pages}` placeholder, unlike spec 8.2's own example ("34 pp →
    // band C"): `RequestScholar` carries `category_band` but not the
    // request's `total_pages` (that lives on `ReviewRequest`, one level
    // up), and `scholarCardFor(t, scholar)`'s signature — fixed by this
    // task's brief — takes no second argument to supply it. Naming this
    // in the report rather than silently inventing a wider signature.
    reasonBand: "Priced at band {band}",
    // The same band, for a review that is NOT priced at it. `resolution.rs`
    // computes and sends the band for every resolution (spec 5.3), but
    // `srb_employee`, `retainer_inclusive`, `zero_value_review` and a
    // monthly quota inside its allowance are all priced at zero — and
    // `reasonBand` beside a "No charge" badge would be a confidently wrong
    // statement about a number the client is invoiced for. The band still
    // earns a mention; it just is not the price.
    reasonBandNoCharge: "Category band {band} · no charge for this review",
    reasonFixedValue: "Priced at {value} {currency}",
    reasonQuota: "Monthly quota {used} of {allowance} used",
    reasonQuotaCharged:
      "Monthly quota {used} of {allowance} used · this review is charged at band {band}",
    reasonQuotaChargedFixed:
      "Monthly quota {used} of {allowance} used · this review is charged at {value} {currency}",
    blockedNoTerms:
      "This scholar has no engagement terms. Add terms before sending this request.",
    blockedNotOnRoster:
      "This scholar is not on this client's board and carries no monthly quota. Add them to the roster, or give them quota terms.",
    blockedNoBand:
      "No category band covers this request's page count. Check the band thresholds before sending.",
    blockedNoDocuments:
      "Select at least one document before this review can be priced.",
    ssbUntickLabel:
      "This scholar is not acting as a board member on this review",
    ssbUntickReason: "Reason (required)",
    ssbCannotAdd:
      "Board membership comes from the client's roster and cannot be set here. Add the scholar to the roster instead.",
    // The rest of the untick control (Task 12), kept beside the three keys
    // above deliberately: they are ONE control, and a label that drifts away
    // from the field it names is how a required justification stops reading as
    // required.
    ssbUntickSubmit: "Remove board membership on this review",
    ssbUntickCancel: "Keep board membership",
    // `put_draft_scholar` refuses a blank reason with a 422 ("a justification
    // nobody wrote is one nobody can review"). Said BEFORE the round trip,
    // because a SAM who typed nothing learns nothing from a server refusal.
    ssbUntickRequired:
      "Give a short reason for removing this scholar's board membership.",
    // What a recorded untick reads as afterwards. The reason is shown rather
    // than stored out of sight: it is the attributable half of the decision,
    // and the whole reason spec 5.4 demands one.
    ssbUntickRecorded: "Not acting as a board member on this review — {reason}",
    rosterUnreviewed:
      "This client's Shariah Supervisory Board roster has not been reviewed, or has changed since it was. Confirm the roster before sending review requests.",
    submitBlocked:
      "Every scholar must resolve before this request can be sent.",

    // -----------------------------------------------------------------------
    // Phase 2B additions
    // -----------------------------------------------------------------------

    // The rail's four steps (spec 8.1). `composerRules.ts::Step` is the same
    // four identifiers in the same order; the rail is a rail and not a wizard
    // precisely so all four stay on screen while the SAM works.
    steps: {
      product: "Product",
      documents: "Documents",
      scholars: "Scholars",
      confirm: "Confirm",
    },

    // The composer shell itself (spec 8.1): the rail on the left, the active
    // step in the middle, the live summary on the right.
    composer: {
      breadcrumb: "New Review Request",
      title: "New review request",
      // The rail is a landmark; an unnamed `<nav>` is indistinguishable from
      // the page's other navigation to a screen reader.
      railLabel: "Request steps",
      loadError: "Could not open the request composer. Please try again.",
      // The later steps land in Tasks 11-14. Said plainly rather than shown as
      // an empty panel, which reads as a screen that failed to load.
      stepPending: "This step is not available yet.",
      // Spec 5.1 / `handlers/mod.rs::require_current_signoff`: a wrong roster
      // does not error, it produces a confidently wrong purchase order. The
      // whole screen stops, and the heading says which screen fixes it.
      rosterBlockedTitle: "This roster has to be confirmed first",
      rosterBlockedAction: "Open the roster",
      // The footer every step shares. On the document step Next saves a
      // changed selection first, so it says so.
      nav: {
        label: "Step navigation",
        back: "Back",
        next: "Next",
        saveAndContinue: "Save and continue",
        saving: "Saving…",
      },
      // Asked before Back, a link or a step change leaves unsaved document
      // ticks behind (`composer/useLeaveGuard.ts`). The draft itself is resumable via
      // `?draft=`, so only the selection is at stake -- and the body says so,
      // or "leave" reads as losing the whole request.
      leaveConfirm: {
        title: "Leave without saving the documents?",
        body_one:
          "The document you ticked is not saved to this request yet. If you leave now, you will have to select it again. The draft itself is kept.",
        body_other:
          "The {n} documents you ticked are not saved to this request yet. If you leave now, you will have to select them again. The draft itself is kept.",
        leave: "Leave without saving",
        stay: "Stay on this page",
      },
      product: {
        title: "What is being reviewed?",
        intro:
          "The product decides which scholars are offered and, with the page count, what the review is priced at.",
        legend: "Review product",
        start: "Start this request",
        starting: "Starting…",
        // Once the draft exists the product is fixed — there is no route that
        // re-products a draft, and a second draft would strand the first one's
        // quota reservation. Changing it therefore CANCELS this draft, which
        // releases the reservation, and starts a new one; saying so is the
        // difference between an informed click and a lost selection.
        chosen: "Reviewing",
        change: "Change the product",
        changing: "Changing…",
        changeHint:
          "Changing the product discards this draft and starts a new one.",
        changeFailed: "The product could not be changed. Please try again.",
        // Asked only when there IS work to lose. Cancelling the draft is what
        // releases its quota reservation, so the document selection goes with
        // it -- and a selection is minutes of a SAM's folder-by-folder work,
        // which must not vanish behind a button labelled "Change the product".
        changeConfirmTitle: "Discard the documents chosen for this request?",
        changeConfirmBody:
          "Changing the product cancels this draft, and the documents chosen for it are discarded with it. They will have to be selected again.",
        changeConfirmAction: "Discard and change the product",
        ...reviewRequestsProductStepMessages,
      },
      // Mapped from the STATUS `createDraft` answers with, never from the
      // server's own text: only a 409 survives `ApiService` (see
      // `reviewRequests.http.ts`). 422 is the archived-product refusal here —
      // the roster refusal shares that status but is caught before this, by
      // the sign-off read the screen already holds.
      createStatus: {
        forbidden:
          "You do not have permission to compose a request for this client.",
        notFound:
          "This client could not be found. It may be outside your sector.",
        unprocessable:
          "That product cannot be used for a new request — it may have just been archived. Pick another.",
        fallback: "This request could not be started. Please try again.",
      },
    },

    // The right-hand panel (spec 8.1). It fills in as the SAM works, because
    // the two things they never type — the folder the review lands in and the
    // purchase orders it raises — change with every scholar added and every
    // folder opened. A wizard would reveal both at a final step nobody reads
    // carefully; the panel keeps them on screen from the first scholar onward.
    summary: {
      title: "This request",
      // Spec 6.2: "This is stated on screen in a persistent bar, because it is
      // a consequence they are choosing without realising they are choosing
      // it." The label IS the sentence; the value is the folder.
      folder: "This review will be created in",
      folderPending: "No folder chosen yet.",
      product: "Product",
      productPending: "No product chosen yet.",
      documents: "Documents",
      documentsPending: "No documents selected yet.",
      scholars: "Scholars",
      scholarsPending: "No scholars added yet.",
      scholarsValue_one: "1 scholar",
      scholarsValue_other: "{n} scholars",
      // The LABEL only. The value is `confirm.purchaseOrders_one/_other`, the
      // same sentence the confirm step reads -- see the module doc of
      // `reviewRequests.confirm.ts` for why this panel no longer has a wording
      // of its own. `purchaseOrdersNone` was already borrowed from there.
      purchaseOrders: "Purchase orders",
    },

    explorer: {
      title: "Choose the folder and the documents",
      chooseDrive: "Choose a document library",
      // Task 9. The breadcrumb's accessible name, and the folder rows' — a
      // `<nav>` with no name is one of several unnamed landmarks to a screen
      // reader, and a row button whose whole content is "Q3 filings" does not
      // say that activating it opens a folder rather than picks a file.
      breadcrumbLabel: "Folder path",
      openFolder: "Open folder {name}",
      // The bar's Up control (`ExplorerToolbar.vue`). The short label is what
      // shows; the long one is its tooltip and accessible name, and names the
      // shortcut so a keyboard user can find it.
      up: "Up",
      // The first crumb when the SAM can see more than one library: each
      // sector is its own SharePoint library, and this is the list of them.
      libraries: "Libraries",
      upTitle: "Up to the parent folder (Alt+Up)",
      // The "…" that folds a deep path. Read out instead of the three dots.
      hiddenFolders: "Show {count} more folders in the path",
      hiddenFoldersSelected:
        "Show {count} more folders in the path, including the one your selection is in",
      // The folder list's accessible name (`ExplorerList.vue`), which is
      // where focus lands after a navigation.
      contents: "Folder contents",
      // Announced (politely) after every navigation: the list is replaced
      // under a screen-reader user's focus, and nothing else says where they
      // are now.
      opened: "Opened {name}",
      // The folder row's name when it holds the ticked files. The pin inside
      // the button is not read out: an aria-label replaces its content.
      openFolderSelected: "Open folder {name}, {count} selected",
      // Not an error. A delegated token is intersected with the SAM's own
      // SharePoint permissions, so "none" is an ordinary answer for someone
      // who has not been given a review library yet (spec 3.2).
      drivesEmpty:
        "No SharePoint document library is available to you. Ask an administrator to give you access to your sector's review library.",
      folderEmpty: "This folder is empty.",
      loading: "Loading…",
      loadMore: "Show more",
      // Task 11 (Vue-8): the drive picker's own failure had no way back except
      // a full reload. Retries the same `listDrives` call the mount made.
      retry: "Try again",
      selected: "{count} selected",
      // Why a row cannot be picked. The backend sends a CODE
      // (`handlers/explorer.rs::reviewability`); these are the only three it
      // has, and `unknown` covers a fourth it may grow -- a raw identifier must
      // never reach a SAM.
      unreviewable: {
        empty: "This file is empty, so there is nothing to review.",
        too_large:
          "This file is too large for Suhayl to open. Split it, or ask for a smaller copy.",
        unsupported_type:
          "Reviews accept Word, PowerPoint, Excel and PDF files only.",
        unknown: "This file cannot be added to a review.",
      },
      // Spec 6.2 / `handlers/documents.rs`: every document must be a DIRECT
      // child of the one folder the review will be created in. Said before the
      // server has to refuse the set, and it names both ways out, because "no"
      // with no next step is what makes a SAM email support.
      singleFolder:
        "Every document must come from the folder this review will be created in — {folder}. Move the file there, or clear the selection and start again from a different folder.",
      folderChosen: "This review will be created in {folder}.",
      // Mapped from the STATUS `assertedRequest` returns, never from a server
      // message: only a 409's text survives ApiService (see
      // `reviewRequests.http.ts`), and a SharePoint error body can echo an
      // attacker-influenceable file name.
      error: {
        signIn:
          "Your SharePoint sign-in has expired. Sign in again, then try once more.",
        notConfigured: "SharePoint browsing is not configured on this server.",
        forbidden: "You do not have permission to open that in SharePoint.",
        notFound: "That folder or file is no longer there.",
        unexpected: "SharePoint could not be reached. Try again in a moment.",
      },
    },

    // Counts appear next to each file AT SELECTION TIME, because they drive
    // the category band and therefore the money (spec 6.4).
    pageCount: {
      counting: "Counting pages…",
      pages_one: "1 page",
      pages_other: "{n} pages",
      // Not a failure -- tier 3 is the SAM typing the number. A file with no
      // automatic count is still selectable.
      manualNeeded:
        "Suhayl could not count this file. Enter the number of pages.",
      manualLabel: "Page count",
      manualApply: "Save this count",
      manualInvalid: "Enter a whole number of pages.",
      // Where the number came from, so an overridden count is visibly an
      // override rather than an assertion Suhayl made.
      source: {
        app_xml: "Counted from the document",
        pdf_convert: "Counted from a PDF conversion",
        manual: "Entered by hand",
      },
      edit: "Change",
      // Task 14: the visible button text is the bare word "Change" on every
      // row, which a screen reader announces identically for every document
      // in the list. The accessible name names the document instead.
      editAria: "Change the page count for {name}",
      // Both halves are count-dependent, so the caller renders `pages_one` /
      // `pages_other` first and substitutes it in (`documentRules.ts::
      // pageCountTotal`). One key carrying "1 documents · 12 pages" was the
      // shape this replaced — `cspSafeMessageCompiler` does `{named}` only and
      // cannot parse vue-i18n's `|` plural syntax, so every plural in this
      // module is a manual `_one` / `_other` pair.
      total_one: "1 document · {pages}",
      total_other: "{documents} documents · {pages}",
    },

    // The document step (spec 6.4). The explorer occupies the left of it; this
    // copy is the selection beside it, where the page counts appear as each
    // file is ticked.
    documents: {
      title: "Which documents are being reviewed?",
      intro:
        "Tick a file to add it. Suhayl counts its pages as it is added, because the page count decides the category band and what the review is priced at.",
      selectedTitle: "Selected documents",
      // Not an error, and not a blank panel: the step opens here every time.
      none: "No documents selected yet. Tick a file to add it.",
      remove: "Remove {name}",
      band: "band {band}",
      // Silence here would read as "no charge", and the submit would be
      // refused with `no_band` three steps later.
      bandNone: "no category band covers this page count",
      // NOT `bandNone`. The bands could not be READ -- saying nothing covers
      // the count would be a false statement about the client's configuration,
      // and would send the SAM to an admin screen that is not broken.
      bandUnavailable:
        "the category bands could not be read, so the band is not shown",
      awaitingCounts:
        "Every document needs a page count before this selection can be saved.",
      // `DocumentSetPayload.parent_item_id` is a required item id and a drive
      // root has none, so a selection made at the top of a library cannot be
      // sent. Said here rather than discovered as a 400; the files stay ticked
      // and counted, and opening a folder is one click.
      rootFolder:
        "Open a folder inside this library before saving — a review cannot be created in the library's root.",
      // Re-entering the step reopens the saved set in its folder; the path to
      // that folder is one Graph call per level, so it is not instant.
      restoring: "Opening the saved documents…",
      // The way back to the folder failed, not the selection: it is still on
      // the request, and Next carries on with it.
      restoreFailed_one:
        "The saved document is still on this request, but its folder could not be opened. Choose documents again only to replace it.",
      restoreFailed_other:
        "The {n} saved documents are still on this request, but their folder could not be opened. Choose documents again only to replace them.",
      // Navigating out of the selection's folder does not drop the selection.
      // `Explorer.vue` emits `navigate` only AFTER a successful load, so this
      // is necessarily post-hoc: the SAM is already standing somewhere else,
      // and the two ways out are to walk back or to start again here.
      otherFolderTitle: "These documents come from another folder",
      keepSelection:
        "The selection is kept. Go back to {folder} to carry on there.",
      // One click back to the folder the selection lives in, so the notice's
      // first way out is a button rather than directions to follow.
      goToFolder: "Go to {folder}",
      clearSelection: "Clear the selection and use this folder instead",
      // `documents.rs::MAX_DOCUMENTS`, mirrored at the tick. The number is
      // interpolated from the constant rather than spelled out, so the copy
      // cannot drift away from the cap it is describing. `{max}` and not
      // vue-i18n's `|` plural syntax: `cspSafeMessageCompiler` does `{named}`
      // only, and a cap is never 1 anyway.
      atCapTitle: "This selection is full",
      atCap:
        "A review request may carry at most {max} documents. Remove one before adding another.",
      // Mapped from the STATUS the PUT answered with and, on a 422, its
      // `code` -- never from its body, some of whose 422s echo a SharePoint
      // file name. A stale sign-off reuses `rosterUnreviewed`.
      saveStatus: {
        forbidden: "You do not have permission to change this request.",
        notFound:
          "This request could not be found. It may have been cancelled.",
        conflict:
          "These documents could not be saved because of a conflict. Please reload and try again.",
        notADraft:
          "This request has already been sent and can no longer be edited. Reload the page to see it.",
        notInFolder:
          "Every document must come from the folder this review will be created in. Move the file into that folder, or choose a different folder.",
        duplicate:
          "The same document was selected twice. Remove the duplicate and try again.",
        gone: "One of the selected documents is no longer in that folder. Refresh the folder and select them again.",
        unprocessable:
          "These documents could not be saved. One of them may have moved or been renamed, or they may no longer all be in the same folder. Refresh the folder and select them again.",
        fallback: "These documents could not be saved. Please try again.",
      },
    },

    // The confirm step's own copy (Task 13) lives in a sibling file for the
    // same reason the scholars step's does -- this file was at the 400-line
    // limit. The keys are still `reviewRequests.requests.confirm.*`.
    ...reviewRequestsConfirmMessages,

    // The request list's own copy (Task 14) lives in a sibling file for the
    // same reason: the keys are still `reviewRequests.requests.list.*`.
    ...reviewRequestsListMessages,

    // The scholars step's own copy (Task 12) lives in a sibling file purely to
    // keep this one under the 400-line hard limit; the keys are still
    // `reviewRequests.requests.scholars.*`.
    ...reviewRequestsScholarsMessages,

    // The request page's own copy (Task 15), same split, same reason: the keys
    // are still `reviewRequests.requests.detail.*`.
    ...reviewRequestsDetailMessages,

    // Task 14's per-task progress and resend, same split, same reason: the
    // keys are still `reviewRequests.requests.dispatch.*`.
    ...reviewRequestsDispatchMessages,
  },
};
